import Breadcrumbs from "@components/shared/Breadcrumbs/Breadcrumbs";
import { FulfillBackordersModal } from "@components/shared/FulfillBackordersModal/FulfillBackordersModal";
import OwButton from "@components/shared/OwButton/OwButton";
import OwCheckbox from "@components/shared/OwCheckbox/OwCheckbox";
import { OwFnTable } from "@components/shared/OwFnTable/OwFnTable";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwSelectOption from "@components/shared/OwSelectOption/OwSelectOption";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import { useAppContextNonNull } from "@contexts/AppContext";
import IErrorDTO from "@models/DTO/IErrorDTO";
import { IFulfillBackordersByOrderDTO } from "@models/DTO/IFulfillBackordersByOrderDTO";
import { IFulfillBackordersDTO } from "@models/DTO/IFulfillBackordersDTO";
import IGetCatalogsDTO from "@models/DTO/IGetCatalogsDTO";
import InventoryAllocationStrategyEnum from "@models/DTO/InventoryAllocationStrategyEnum";
import IOnGetBackorderFulfillmentByOrderResponseDTO from "@models/DTO/IOnGetBackorderFulfillmentByOrderResponseDTO";
import IOnGetCatalogsDTO from "@models/DTO/IOnGetCatalogsDTO";
import EndpointsEnum from "@models/EndpointsEnum";
import IBackorderFulfillmentOrderDetail from "@models/IBackorderFulfillmentOrderDetail";
import ICatalog from "@models/ICatalog";
import INotification from "@models/INotification";
import IOwTableCols from "@models/IOwTableCols";
import IUiSocketSetup from "@models/IUiSocketSetup";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import NotificationPositionEnum from "@models/NotificationPositionEnum";
import RestEndpointsEnum from "@models/RestEndpointsEnum";
import { buildRestUrl, buildAuthHeadersForFetch, buildRestUrlWithQuery } from "@services/RestService";
import UIService from "@services/UIService";
import { startCase } from "lodash-es";
import React, { ChangeEvent, useEffect, useState, useMemo, useCallback } from "react";
import { FormattedNumber } from "react-intl";
import { Link, useParams } from "react-router-dom";

type Props = {};

export const InventoryBackorders = (props: Props) => {
    const { supplierModel, userModel, vendorModel, socket, uiContext } = useAppContextNonNull();

    const [isDataFetching, setIsDataFetching] = useState(true);
    const [isInitialData, setIsInitialData] = useState(true);
    const [backorderAnalysis, setBackorderAnalysis] = useState<IOnGetBackorderFulfillmentByOrderResponseDTO>();
    const [inventoryAllocationStrategy, setInventoryAllocationStrategy] = useState<InventoryAllocationStrategyEnum>(
        InventoryAllocationStrategyEnum.FIFO
    );
    const [checkedRows, setCheckedRows] = useState<string[]>([]);
    const [selectAll, setSelectAll] = useState(false);
    const [isFulfillmentProcessing, setIsFulfillmentProcessing] = useState(false);
    const [selectedCatalog, setSelectedCatalog] = useState<ICatalog | null>(null);
    const [catalogs, setCatalogs] = useState<ICatalog[]>([]);

    const { catalogGuid } = useParams();

    useEffect(() => {
        if (catalogGuid || selectedCatalog) {
            getBackorderAnalysis();
        }
    }, [selectedCatalog, catalogs]);

    const onGetCatalogs = (data: IOnGetCatalogsDTO) => {
        setCatalogs(data.catalogs);

        for (const catalog of data.catalogs) {
            if (catalogGuid) {
                if (catalog.guid === catalogGuid) {
                    setSelectedCatalog(catalog);
                }
            } else {
                if (supplierModel.default_order_catalog_id === catalog.id) {
                    setSelectedCatalog(catalog);
                }
            }
        }
    };

    const socketSetup: IUiSocketSetup[] = [
        {
            endpoint: EndpointsEnum.CatalogsOnGetCatalogs,
            fn: onGetCatalogs,
        },
    ];

    const getCatalogs = async () => {
        const payload: IGetCatalogsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.CatalogsGetCatalogs, payload);
    };

    useEffect(() => {
        socketSetup.forEach((setup) => {
            socket.on(setup.endpoint, setup.fn);
        });

        getCatalogs();

        return () => {
            socketSetup.forEach((setup) => {
                socket.removeListener(setup.endpoint, setup.fn);
            });
        };
    }, []);

    const getBackorderAnalysis = async () => {
        if (!catalogGuid && !selectedCatalog) {
            return null;
        }

        setIsDataFetching(true);

        const url = buildRestUrlWithQuery({
            endpoint: RestEndpointsEnum.InventoryBackorderAnalysisByOrder,
            params: {
                catalogGuid: selectedCatalog?.guid || catalogGuid || "",
            },
            query: {
                allocationStrategy: inventoryAllocationStrategy,
            },
        });

        const headers = buildAuthHeadersForFetch({
            userModel,
            supplierModel,
            vendorModel: !userModel.isSupplierUser ? vendorModel : undefined,
        });

        await fetch(url, {
            method: "GET",
            headers,
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Failed to fetch backorder analysis");
                }
                return response.json();
            })
            .then(async (data) => {
                console.log(data);

                // const jsonData = await data.json();

                // console.log(jsonData);

                setBackorderAnalysis(data);
                setCheckedRows([]);
                setSelectAll(false);
            })
            .catch((error) => {
                console.error(error);
            })
            .finally(() => {
                setIsDataFetching(false);
                setIsInitialData(false);
            });
    };

    const closeModal = () => {
        uiContext.setModalComponent(null);
        uiContext.showModal(false);
    };

    const fulfillSelectedOrders = async (_payload: Partial<IFulfillBackordersByOrderDTO>) => {
        console.log("Fulfilling selected orders");

        const payload: IFulfillBackordersByOrderDTO = {
            orderGuids: checkedRows,
            autoFulfill: _payload.autoFulfill,
            createBackorderForRemaining: _payload.createBackorderForRemaining,
            useHeldInventoryFirst: _payload.useHeldInventoryFirst,
            allocationStrategy: inventoryAllocationStrategy,
        };

        const url = buildRestUrl({
            endpoint: RestEndpointsEnum.InventoryFulfillBackordersByOrder,
        });

        const headers = buildAuthHeadersForFetch({
            userModel,
            supplierModel,
            vendorModel: !userModel.isSupplierUser ? vendorModel : undefined,
        });

        fetch(url, {
            method: "POST",
            headers,
            body: JSON.stringify(payload),
        })
            .then((response) => {
                if (!response.ok) {
                    const errorDTO: IErrorDTO = {
                        eid: "",
                        title: "error_fulfill_backorders",
                        message: response.statusText,
                    };

                    uiContext.notifyFn(errorDTO);
                }
                return response.json();
            })
            .then((data) => {
                const notification: INotification = {
                    message: "backorder_fulfillment_success",
                    level: NotificationLevelEnum.success,
                    position: NotificationPositionEnum.bc,
                };

                uiContext.notifyFn(null, notification);
            })
            .catch((error) => {
                const errorDTO: IErrorDTO = {
                    eid: "",
                    title: "error_fulfill_backorders",
                    message: error.message,
                };

                uiContext.notifyFn(errorDTO);
            })
            .finally(() => {
                setIsFulfillmentProcessing(false);
                uiContext.setModalComponent(null);
                uiContext.showModal(false);
            });
    };

    const showFulfillModal = async () => {
        const modalComponent = <FulfillBackordersModal closeFn={closeModal} fulfillFn={fulfillSelectedOrders} />;

        uiContext.setModalComponent(modalComponent);
        uiContext.showModal(true);
    };

    const toggleRowSelection = useCallback((orderGuid: string) => {
        console.log("Toggling selection for:", orderGuid);
        setCheckedRows((prev) => {
            const index = prev.indexOf(orderGuid);
            if (index > -1) {
                // Remove from array if already selected
                return prev.filter((id) => id !== orderGuid);
            } else {
                // Add to array maintaining order
                return [...prev, orderGuid];
            }
        });
    }, []);

    const toggleSelectAll = () => {
        if (selectAll) {
            setCheckedRows([]);
        } else {
            const allOrderGuids = backorderAnalysis?.orders.map((order) => order.orderGuid) || [];
            setCheckedRows(allOrderGuids);
        }
        setSelectAll(!selectAll);
    };

    const renderActionCol = useCallback(
        (row: any) => {
            const isChecked = checkedRows.includes(row.orderGuid);

            return (
                <div>
                    <OwCheckbox
                        fn={(e: React.ChangeEvent<HTMLInputElement>) => {
                            toggleRowSelection(row.orderGuid);
                        }}
                        checked={isChecked}
                        truthyVal={true}
                        falsyVal={false}
                        name={`checkbox-${row.orderGuid}`}
                    />
                </div>
            );
        },
        [checkedRows, toggleRowSelection]
    );

    const renderOrderCol = useCallback((row: IBackorderFulfillmentOrderDetail) => {
        return (
            <div>
                <Link to={`/orders/${row.orderGuid}/entry`}>{row.orderName}</Link>
            </div>
        );
    }, []);

    const renderAccountCol = useCallback((row: IBackorderFulfillmentOrderDetail) => {
        return (
            <div>
                <Link to={`/account/${row.accountGuid}/detail`}>{row.accountName}</Link>
            </div>
        );
    }, []);

    const renderFulfillmentPercentageCol = useCallback((row: IBackorderFulfillmentOrderDetail) => {
        return (
            <div>
                <FormattedNumber value={row.pendingItemFulfillmentPercentage} maximumFractionDigits={supplierModel.qty_precision || 2} />%
            </div>
        );
    }, []);

    const renderTotalPendingItemsCol = useCallback((row: IBackorderFulfillmentOrderDetail) => {
        return <div>{row.totalPendingItems}</div>;
    }, []);

    const renderFulfillableItemsCol = useCallback((row: IBackorderFulfillmentOrderDetail) => {
        return <div>{row.fulfillableItems}</div>;
    }, []);

    // Define columns as a useMemo to prevent closure issues
    const tableColumns = useMemo(() => {
        return [
            {
                title: "",
                valueProp: "",
                renderFn: renderActionCol,
                renderFnEmitsRow: true,
                isActionsCol: true,
                className: "w-5r",
                hasSelectAll: true,
                selectAllFn: toggleSelectAll,
            },
            {
                title: "order",
                valueProp: "orderName",
                className: "w-15r",
                renderFn: renderOrderCol,
                renderFnEmitsRow: true,
            },
            {
                title: "account",
                valueProp: "accountName",
                renderFn: renderAccountCol,
                renderFnEmitsRow: true,
            },
            {
                title: "pending_fulfillment_percentage",
                valueProp: "fulfillmentPercentage",
                renderFn: renderFulfillmentPercentageCol,
                renderFnEmitsRow: true,
                className: "w-15r",
            },
            {
                title: "total_pending_items",
                valueProp: "totalPendingItems",
                className: "w-15r",
                renderFn: renderTotalPendingItemsCol,
                renderFnEmitsRow: true,
            },
            {
                title: "fulfillable_items",
                valueProp: "fulfillableItems",
                className: "w-15r",
                renderFn: renderFulfillableItemsCol,
                renderFnEmitsRow: true,
            },
        ] as IOwTableCols[];
    }, [
        checkedRows,
        selectAll,
        renderActionCol,
        toggleSelectAll,
        renderFulfillmentPercentageCol,
        renderTotalPendingItemsCol,
        renderFulfillableItemsCol,
    ]); // Re-create columns when selection state changes

    useEffect(() => {
        getBackorderAnalysis();
    }, [catalogGuid]);

    useEffect(() => {
        if (backorderAnalysis?.orders) {
            const allSelected =
                backorderAnalysis.orders.length > 0 && backorderAnalysis.orders.every((order) => checkedRows.includes(order.orderGuid));
            setSelectAll(allSelected);
        }
    }, [checkedRows, backorderAnalysis]);

    return (
        <div className={"InventoryBackorders mt-2"}>
            <Breadcrumbs
                crumbs={[
                    {
                        title: "inventory",
                        url: "/inventory",
                    },
                    {
                        title: "backorders",
                        url: `/inventory/backorders/${catalogGuid}`,
                    },
                ]}
            />

            <OwTitle leftIconName="fas fa-truck-loading" title={"backorder_analysis"} tag={"h2"} />

            <div className={`my-4 card ${UIService.isMainViewLight() ? "" : "bg-dark text-light"}`}>
                <div className="card-body d-flex flex-row gap-2 align-items-end">
                    <div>
                        <div>
                            <OwLabel title={"catalog"} />
                        </div>
                        <div>
                            <OwSelectOption
                                name={"catalog"}
                                options={catalogs}
                                valueProp={"guid"}
                                titleProp={"name"}
                                selectedOption={selectedCatalog}
                                selectedOptionProp="guid"
                                emitObject={true}
                                fn={(catalog: ICatalog) => {
                                    setSelectedCatalog(catalog);
                                }}
                            />
                        </div>
                    </div>
                    <div>
                        <div>
                            <OwLabel title="strategy" />
                        </div>
                        <OwSelectOption
                            name={"inventory_allocation_strategy"}
                            options={Object.values(InventoryAllocationStrategyEnum)
                                .filter((strategy) => strategy === InventoryAllocationStrategyEnum.FIFO)
                                .map((strategy) => {
                                    let label = startCase(strategy);

                                    if (label === "Fifo") {
                                        label = "FIFO";
                                    }

                                    return {
                                        label,
                                        value: strategy,
                                    };
                                })}
                            selectedOption={inventoryAllocationStrategy}
                            hasSelectedOptionProp={false}
                            fn={(e: ChangeEvent<HTMLSelectElement>) => {
                                console.log(e.target.value);
                                setInventoryAllocationStrategy(e.target.value as InventoryAllocationStrategyEnum);
                            }}
                            titleProp={"label"}
                            valueProp={"value"}
                            translateTitle={false}
                        />
                    </div>

                    <div>
                        <OwButton title={"get_analysis"} fn={getBackorderAnalysis} active={isDataFetching} disableOnActive={true} />
                    </div>

                    <div>
                        <OwButton
                            title={"fulfill_orders"}
                            fn={() => {
                                showFulfillModal();
                            }}
                            active={isFulfillmentProcessing}
                            disableOnActive={true}
                            disabled={checkedRows.length === 0}
                        />
                    </div>
                </div>
            </div>

            <div>
                <OwFnTable
                    tableClassName="table-hover"
                    columns={tableColumns}
                    dataSet={backorderAnalysis ? backorderAnalysis.orders : []}
                    dataFetch={isDataFetching}
                    initialData={isInitialData}
                />
            </div>
        </div>
    );
};
